import type {
  CustomerCatalogueItemDetail,
  CustomerCatalogueOption,
  CustomerPriceQuote,
  CustomerQuantityLadderRow,
} from "../data/customer-api-types";
import {
  customerOrderErrorDetail,
  isPriceBlockedError,
} from "./customer-order-error";
import { customerLabelOf } from "./customer-option-wording";
import type { ListQueryState } from "./useListQuery";

/**
 * Where the catalogue item page's price stands, from the price route's answer. Every answer ends in one of
 * these; "pending" is only a request still in flight (the page gives up waiting on it separately, see
 * `useIsStalled`). The page draws the price, the quantity ladder and the Add to basket button from this and
 * nothing else, so a response shape this does not name cannot leave the button waiting.
 */
export type CatalogueItemPrice =
  | { kind: "pending" }
  | {
      kind: "priced";
      price: CustomerPriceQuote;
      ladder: CustomerQuantityLadderRow[];
      /** The price already includes delivery (a supplier that delivers); otherwise delivery is chosen at checkout. */
      isDeliveryIncluded: boolean;
    }
  /** Answered, with no figure for this selection: the person cannot order it online. */
  | { kind: "price_on_request"; ladder: CustomerQuantityLadderRow[] }
  /**
   * A paper they typed, which our team prices (spec 32 §5.7): no price, but the line goes in the basket as
   * "Quote on request" and the basket is sent to us for a price. `reason` is the dictionary's words for why.
   */
  | { kind: "quote_on_request"; reason: string }
  /** The "needs approval before ordering online" refusal. */
  | { kind: "blocked"; message: string }
  /** The price route failed (or refused the combination): there is no price for this selection. */
  | { kind: "failed"; message: string; reasonCode: string | null };

const FAILED_FALLBACK =
  "We couldn't price this selection. Please try again or choose a different option.";
const BLOCKED_FALLBACK =
  "This item can't be priced online right now — please contact your rep.";

/**
 * The headline price when the response carries one, else the selected quantity's own priced ladder row.
 *
 * The headline is the price hierarchy's suggested level, and the server leaves it out (`price: null`) for a
 * customer with no agreed rate and no order history for the item, though the ladder beside it is priced.
 * The selected row is the same quote's total, so it is the price the person is looking at; it is shown as
 * the standard price, which is all it is.
 */
function headlinePrice(
  item: CustomerCatalogueItemDetail,
  quantity: number,
): CustomerPriceQuote | null {
  if (item.price) return item.price;
  const row = (item.quantity_ladder ?? []).find((r) => r.quantity === quantity);
  if (!row || !row.available || row.total_pence == null) return null;
  return {
    price_pence: row.total_pence,
    unit_price_pence: row.unit_price_pence ?? row.total_pence / quantity,
    basis: "standard",
    basis_date: null,
  };
}

/** Reads the price query's state for the selected quantity into the one outcome the page shows. */
export function resolveCatalogueItemPrice(
  state: ListQueryState<CustomerCatalogueItemDetail | null>,
  quantity: number | null,
): CatalogueItemPrice {
  if (state.status === "loading") return { kind: "pending" };
  if (state.status === "error") {
    if (isPriceBlockedError(state.error)) {
      return {
        kind: "blocked",
        message: customerOrderErrorDetail(state.error, BLOCKED_FALLBACK)
          .message,
      };
    }
    // Only the server's own customer-safe sentence is shown; any other error's text (a network failure, a
    // bare 500) is not for the customer to read.
    const detail = customerOrderErrorDetail(state.error, FAILED_FALLBACK);
    return {
      kind: "failed",
      message: detail.reasonCode ? detail.message : FAILED_FALLBACK,
      reasonCode: detail.reasonCode,
    };
  }
  // No request was made (the item or quantity is not settled yet).
  if (state.data === null || quantity === null) return { kind: "pending" };

  if (state.data.quote_on_request) {
    return { kind: "quote_on_request", reason: state.data.quote_on_request };
  }
  const ladder = state.data.quantity_ladder ?? [];
  const price = headlinePrice(state.data, quantity);
  if (!price) return { kind: "price_on_request", ladder };
  return {
    kind: "priced",
    price,
    ladder,
    isDeliveryIncluded: state.data.delivery?.status === "included",
  };
}

/** The customer's names for the options with no choice made (an option with no default, not yet picked). */
export function unchosenOptionLabels(
  options: CustomerCatalogueOption[],
  configuration: Record<string, string>,
): string[] {
  return options
    .filter((option) => (configuration[option.key] ?? "") === "")
    .map(customerLabelOf);
}

/**
 * Why Add to basket cannot be pressed yet, in words for the person; null when it can. Always names the
 * thing to do or wait for, so the button is never disabled with no way forward.
 */
export function addToBasketBlockedReason({
  outcome,
  unchosenOptions,
  isStalled,
}: {
  outcome: CatalogueItemPrice;
  unchosenOptions: string[];
  isStalled: boolean;
}): string | null {
  if (outcome.kind === "priced" || outcome.kind === "quote_on_request")
    return null;
  if (unchosenOptions.length > 0) {
    return `Choose ${unchosenOptions.join(", ")} to see your price.`;
  }
  switch (outcome.kind) {
    case "pending":
      return isStalled
        ? "Add to basket is available once your price is ready."
        : "Working out your price…";
    case "price_on_request":
      return "This selection can't be ordered online yet, because it has no price to add.";
    case "failed":
      return "Add to basket is available once we've priced this selection.";
    case "blocked":
      return "This item can't be ordered online right now.";
  }
}
